/**
 * Core coordinate mapping — brief §8.
 * World units are tile units; the d3 ZoomTransform maps world-pixels
 * (world · tileSize) to screen pixels.
 */
import { apply, invert, transformToMatrix, type Mat } from '@/geometry/affine';
import { pointInPolygon } from '@/geometry/wrap';
import type { TileTransform } from '@/patterns/types';
import { useCanvasStore } from '@/stores/canvas';
import { usePatternStore } from '@/stores/pattern';
import { computed } from 'vue';

export interface Placement {
  transform: TileTransform;
  matrix: Mat;
  tilePoint: [number, number];
}

const MAX_TRANSFORMS = 1600;

export function useWrapTransform() {
  const patternStore = usePatternStore();
  const canvasStore = useCanvasStore();

  /** Rotate a screen-space point about the viewport centre by `rad` (Feature 1
   *  — canvas rotation). Centre-pivoted so the pivot tracks resizes for free,
   *  with no stored pivot point needed. */
  function rotateAboutCentre(sx: number, sy: number, rad: number): [number, number] {
    if (rad === 0) return [sx, sy];
    const cx = canvasStore.canvasWidth / 2;
    const cy = canvasStore.canvasHeight / 2;
    const dx = sx - cx;
    const dy = sy - cy;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
  }

  /** Un-rotate a screen-space delta vector (e.g. a two-finger drag's raw pixel
   *  movement) by the current canvas rotation, landing it in the same
   *  UNROTATED frame that `viewTransform.{x,y}` live in — see worldToScreen
   *  below, which rotates that translation back to real screen space at
   *  render time. Deltas are translation-invariant so, unlike
   *  rotateAboutCentre, no pivot point is involved — same rotation formula,
   *  just without the recentring. */
  function unrotateScreenDelta(dx: number, dy: number): [number, number] {
    const rad = -canvasStore.rotation;
    if (rad === 0) return [dx, dy];
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return [dx * cos - dy * sin, dx * sin + dy * cos];
  }

  function screenToWorld(sx: number, sy: number): [number, number] {
    const t = canvasStore.viewTransform;
    const ts = patternStore.tileSize;
    // Undo the rotation first (screen → unrotated screen), then the existing
    // translate+scale mapping (unrotated screen → world) — the exact inverse
    // of worldToScreen's order below.
    const [ux, uy] = rotateAboutCentre(sx, sy, -canvasStore.rotation);
    return [(ux - t.x) / (t.k * ts), (uy - t.y) / (t.k * ts)];
  }

  function worldToScreen(wx: number, wy: number): [number, number] {
    const t = canvasStore.viewTransform;
    const ts = patternStore.tileSize;
    const ux = wx * ts * t.k + t.x;
    const uy = wy * ts * t.k + t.y;
    return rotateAboutCentre(ux, uy, canvasStore.rotation);
  }

  /** Find the placement whose tile contains the world point. */
  function findPlacement(wx: number, wy: number): Placement | null {
    const polygon = patternStore.currentPattern.polygon();
    const rings = Math.min(12, Math.ceil((Math.max(Math.abs(wx), Math.abs(wy)) + 1) * 1.5));
    for (const t of patternStore.currentVariant.transformsInRange(rings)) {
      const m = transformToMatrix(t);
      const p = apply(invert(m), [wx, wy]);
      if (pointInPolygon(polygon, p[0], p[1], 1e-9)) {
        return { transform: t, matrix: m, tilePoint: p };
      }
    }
    return null;
  }

  /** Ring count needed to fill the current viewport (count-capped to the
   *  full transform set, which is the larger of the two). */
  const visibleRings = computed<number>(() => {
    const variant = patternStore.currentVariant;
    const w = canvasStore.canvasWidth;
    const h = canvasStore.canvasHeight;
    if (w === 0 || h === 0) return 1;
    const [x0, y0] = screenToWorld(0, 0);
    const [x1, y1] = screenToWorld(w, h);
    const maxAbs = Math.max(Math.abs(x0), Math.abs(x1), Math.abs(y0), Math.abs(y1));
    let rings = Math.max(1, Math.ceil((maxAbs + 1) * 1.4));
    let count = variant.transformsInRange(rings).length;
    while (count > MAX_TRANSFORMS && rings > 1) {
      rings = Math.max(1, Math.floor(rings * Math.sqrt(MAX_TRANSFORMS / count)));
      count = variant.transformsInRange(rings).length;
    }
    return rings;
  });

  /** Placements needed to fill the current viewport (count-capped). */
  const visibleTransforms = computed<TileTransform[]>(() =>
    patternStore.currentVariant.transformsInRange(visibleRings.value),
  );

  /** Cell-polygon placements (background + outline) — op-invariant stabilizer
   *  copies collapsed out so each lattice cell is placed once. Falls back to
   *  the full transform set for ordinary variants. */
  const visibleCellTransforms = computed<TileTransform[]>(() => {
    const variant = patternStore.currentVariant;
    const cell = variant.cellTransformsInRange ?? variant.transformsInRange;
    return cell.call(variant, visibleRings.value);
  });

  return {
    screenToWorld,
    worldToScreen,
    unrotateScreenDelta,
    findPlacement,
    visibleRings,
    visibleTransforms,
    visibleCellTransforms,
  };
}
